import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/base/SEOHead';
import { localBusinessSchema, buildBreadcrumbSchema, buildGraphSchema } from '@/components/base/schemas';
import { Link } from 'react-router-dom';
import { useState, FormEvent } from 'react';

const breadcrumbSchema = buildBreadcrumbSchema([
  { name: 'Accueil', item: 'https://fenetre-paris15.fr/' },
  { name: 'Devis', item: 'https://fenetre-paris15.fr/devis-fenetre-paris-15/' },
]);

export default function DevisPage() {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setSubmitted(true);
      return;
    }

    setLoading(true);
    const formData = new FormData(form);
    try {
      const data: Record<string, string> = {};
      formData.forEach((value, key) => { data[key] = value as string; });
      const res = await fetch('https://formspree.io/f/mrewqkaw', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Erreur réseau');
      setSubmitted(true);
      form.reset();
    } catch {
      setError('Une erreur est survenue. Veuillez réessayer ou nous appeler directement.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <SEOHead
        title="Devis Réparation Fenêtre Paris 15 — Gratuit & Rapide | DF PRO"
        description="Demandez votre devis gratuit de réparation de fenêtre dans le 15ᵉ. Réponse rapide, prix fixe après diagnostic, sans engagement. ☎ 01 87 66 81 55"
        canonicalUrl="https://fenetre-paris15.fr/devis-fenetre-paris-15/"
        schema={buildGraphSchema(localBusinessSchema, breadcrumbSchema)}
      />

      <div className="bg-brand-mint/30 py-3">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav aria-label="Fil d'Ariane" className="text-xs text-brand-text/60">
            <Link to="/" className="hover:text-brand-accent">Accueil</Link><span className="mx-2">/</span><span className="text-brand-accent font-medium">Devis</span>
          </nav>
        </div>
      </div>

      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-brand-dark mb-4">Devis gratuit pour la réparation de vos fenêtres — Paris 15ᵉ</h1>
            <p className="text-brand-text/70 text-base max-w-2xl mx-auto">
              Décrivez-nous votre problème. Nous vous recontactons rapidement pour convenir d'un rendez-vous de diagnostic. Devis gratuit, sans engagement.
            </p>
          </div>

          {/* Réassurance badges */}
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {[
              { icon: 'ri-check-double-line', text: 'Gratuit et sans engagement' },
              { icon: 'ri-timer-line', text: 'Réponse sous 24/48h' },
              { icon: 'ri-shield-check-line', text: 'Société assurée RC Pro' },
            ].map((b, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 bg-brand-mint text-brand-accent text-xs font-medium px-3 py-1.5 rounded-full">
                <i className={b.icon}></i> {b.text}
              </span>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-10 items-start">
            {/* Image rassurante */}
            <div className="lg:col-span-2">
              <div className="aspect-[3/4] lg:aspect-[4/5] rounded-2xl overflow-hidden lg:sticky lg:top-24">
                <img
                  src="/images/dfpro-devis.webp"
                  alt="Fenêtre réparée dans intérieur parisien lumineux — devis gratuit DF PRO Paris 15"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Formulaire */}
            <div className="lg:col-span-3">
              {submitted ? (
                <div className="bg-brand-mint/50 rounded-2xl p-8 md:p-10 text-center animate-fade-in">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-brand-accent text-white flex items-center justify-center">
                    <i className="ri-check-line text-2xl"></i>
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-brand-dark mb-3">Merci pour votre demande !</h2>
                  <p className="text-brand-text/70 mb-6">Nous avons bien reçu votre demande de devis. DF PRO vous recontacte sous 24 à 48h pour convenir d'un rendez-vous de diagnostic.</p>
                  <p className="text-brand-text/50 text-sm mb-4">Vous préférez nous appeler ?</p>
                  <a href="tel:+33187668155" className="btn-primary text-sm inline-flex items-center gap-2">
                    <i className="ri-phone-line"></i> 01 87 66 81 55
                  </a>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="" className="bg-brand-mint/30 rounded-2xl p-6 md:p-10">
                  {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">{error}</div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 mb-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-brand-dark mb-1.5">Nom complet *</label>
                      <input type="text" id="name" name="name" required className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-brand-text focus:outline-none focus:border-brand-accent transition-colors" placeholder="Votre nom et prénom" />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-brand-dark mb-1.5">Téléphone *</label>
                      <input type="tel" id="phone" name="phone" required className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-brand-text focus:outline-none focus:border-brand-accent transition-colors" placeholder="01 87 66 81 55" />
                    </div>
                  </div>

                  <div className="mb-5">
                    <label htmlFor="email" className="block text-sm font-medium text-brand-dark mb-1.5">E-mail</label>
                    <input type="email" id="email" name="email" className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-brand-text focus:outline-none focus:border-brand-accent transition-colors" placeholder="votre@email.fr" />
                  </div>

                  <div className="mb-5">
                    <label htmlFor="intervention" className="block text-sm font-medium text-brand-dark mb-1.5">Type d'intervention *</label>
                    <select id="intervention" name="intervention" required className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-brand-text focus:outline-none focus:border-brand-accent transition-colors appearance-none cursor-pointer">
                      <option value="">Sélectionnez...</option>
                      <option value="PVC">Fenêtre PVC</option>
                      <option value="Aluminium">Fenêtre Aluminium</option>
                      <option value="Bois">Fenêtre Bois</option>
                      <option value="Volet roulant">Volet roulant</option>
                      <option value="Baie vitrée">Baie vitrée</option>
                      <option value="Plusieurs">Plusieurs interventions</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>

                  <div className="mb-5">
                    <label htmlFor="message" className="block text-sm font-medium text-brand-dark mb-1.5">Décrivez le problème</label>
                    <textarea id="message" name="message" rows={4} maxLength={500} className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-brand-text focus:outline-none focus:border-brand-accent transition-colors resize-none" placeholder="Décrivez la panne : fenêtre qui ferme mal, volet bloqué, poignée cassée..."></textarea>
                    <p className="text-xs text-brand-text/40 mt-1">500 caractères maximum</p>
                  </div>

                  {/* Formspree subject */}
                  <input type="hidden" name="_subject" value="Nouvelle demande - Devis - fenetre-paris15.fr" />
                  {/* Honeypot */}
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hp-field" />

                  <button type="submit" disabled={loading} className="btn-primary w-full justify-center text-base py-4 disabled:opacity-50">
                    {loading ? (
                      <><i className="ri-loader-4-line animate-spin"></i> Envoi en cours...</>
                    ) : (
                      <><i className="ri-send-plane-line"></i> Envoyer ma demande de devis</>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Alternative contact */}
        <div className="max-w-6xl mx-auto px-4 md:px-6 mt-10">
          <div className="text-center p-6 bg-brand-mint/30 rounded-2xl">
            <h3 className="font-heading font-bold text-brand-dark text-lg mb-2">Vous préférez nous appeler ?</h3>
            <p className="text-brand-text/60 text-sm mb-4">Nos techniciens sont disponibles du lundi au vendredi de 8h à 19h et le samedi de 9h à 14h.</p>
            <a href="tel:+33187668155" className="btn-outline text-sm inline-flex items-center gap-2">
              <i className="ri-phone-line"></i> 01 87 66 81 55
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}